import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, Page, PageHeader, TableSkeleton } from "../../../components/ui";
import { QUILL_FORMATS, QUILL_MODULES } from "../../../components/editor/quillConfig";
import { getSubscriptionPlans, updateSubscriptionPlan } from "../../../services/packages";
import { isBlank } from "../legal/LegalTextPage";
import "../legal/legal.css";
import "./pricing.css";

const TYPES = [
  { type: "monthly", label: "Monthly", per: "month" },
  { type: "annual", label: "Annual", per: "year" },
];

const money = (value, currency = "USD") =>
  value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: String(currency).toUpperCase(),
        maximumFractionDigits: String(currency).toUpperCase() === "XOF" ? 0 : 2,
      }).format(value);

/** Error translation key of a plan draft, or null. */
export const planError = (draft) =>
  !draft.name.trim() || !(Number(draft.price) >= 0.5) || isBlank(draft.description)
    ? "Please Enter Complete Package Details First!"
    : null;

/** Where the price applies, as shown under each plan. */
const Applies = ({ plan, usdToCfa }) => {
  const { t } = useTranslation();
  const card = plan.card || {};
  const cardMatches = card.amount !== null && Math.abs(card.amount - plan.price) < 0.005;
  return (
    <ul className="plan-applies">
      <li>
        <i className="ri-bank-card-line" aria-hidden="true"></i>
        <span>{t("Card (Stripe)")}</span>
        {card.error ? (
          <Badge color="red">{t("Unavailable")}</Badge>
        ) : (
          <>
            <strong>{money(card.amount, card.currency || "USD")}</strong>
            {!cardMatches && <Badge color="amber">{t("Differs")}</Badge>}
          </>
        )}
      </li>
      <li>
        <i className="ri-smartphone-line" aria-hidden="true"></i>
        <span>{t("Mobile Money")}</span>
        <strong>{money(plan.mobileMoney?.XOF, "XOF")}</strong>
        <span className="ui-hint">
          {t("or {{usd}} (1 $ = {{rate}} FCFA)", { usd: money(plan.mobileMoney?.USD), rate: usdToCfa })}
        </span>
      </li>
      <li>
        <i className="ri-global-line" aria-hidden="true"></i>
        <span>{t("Site and apps")}</span>
        <strong>{money(plan.price)}</strong>
      </li>
    </ul>
  );
};

const PlanCard = ({ plan, meta, usdToCfa, onEdit }) => {
  const { t } = useTranslation();
  return (
    <article className="ui-card plan-card" aria-label={t(meta.label)}>
      <header className="plan-card-head">
        <div>
          <span className="ui-label">{t(meta.label)}</span>
          <h2>{plan.name}</h2>
        </div>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onEdit}>
          <i className="ri-pencil-line" aria-hidden="true"></i>
          {t("edit")}
        </button>
      </header>
      <p className="plan-price">
        <strong>{money(plan.price)}</strong>
        <span>/{t(meta.per)}</span>
      </p>
      {plan.type === "annual" && plan.monthlyEquivalent ? (
        <p className="ui-hint">
          {t("or {{amount}} per month", { amount: money(plan.monthlyEquivalent) })}
          {plan.savingsPercent > 0 && ` · ${t("{{percent}} % cheaper than monthly", { percent: plan.savingsPercent })}`}
        </p>
      ) : null}
      <Applies plan={plan} usdToCfa={usdToCfa} />
      {plan.description && (
        <div className="legal-html plan-description" dangerouslySetInnerHTML={{ __html: plan.description }} />
      )}
    </article>
  );
};

/**
 * Subscription plans: the single price of each plan for the whole platform
 * (card through Stripe, Mobile Money, site and apps).
 */
const Package = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // { type, price, name, description }
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSubscriptionPlans()
      .then(setData)
      .catch(() => {
        setData({ plans: {} });
        toast.error(t("Error fetching data"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (plan) =>
    setEditing({ type: plan.type, price: String(plan.price), name: plan.name, description: plan.description });
  const current = editing && data?.plans?.[editing.type];
  const priceChanged = current && Number(editing.price) !== current.price;

  const submit = () => {
    const error = planError(editing);
    if (error) {
      toast.error(t(error));
      return;
    }
    if (priceChanged) setConfirming(true);
    else save();
  };

  const save = async () => {
    setSaving(true);
    try {
      setData(await updateSubscriptionPlan(editing.type, editing));
      setEditing(null);
      toast.success(t("Plan updated everywhere"));
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    } finally {
      setSaving(false);
    }
  };

  const meta = editing && TYPES.find((item) => item.type === editing.type);

  return (
    <Page>
      <PageHeader
        title={t("Subscription plan")}
        subtitle={t("One price per plan for the whole platform: card payments (Stripe), Mobile Money, the site and the apps.")}
      />
      {!data ? (
        <section className="ui-card">
          <TableSkeleton rows={4} />
        </section>
      ) : editing ? (
        <section className="ui-card plan-form">
          <h2 className="plan-form-title">{t(meta.label)}</h2>
          <div className="ui-form">
            <div className="ui-field">
              <label htmlFor="plan-price">{t("Package Price")}</label>
              <div className="ui-input-group">
                <input
                  id="plan-price"
                  className="ui-input"
                  type="number"
                  min="0.5"
                  step="0.01"
                  value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                />
                <span className="ui-input-addon" aria-hidden="true">
                  $/{t(meta.per)}
                </span>
              </div>
              {Number(editing.price) > 0 && (
                <p className="ui-hint">
                  {t("Mobile Money: {{amount}}", {
                    amount: money(Math.ceil((Number(editing.price) * data.usdToCfa) / 5) * 5, "XOF"),
                  })}
                </p>
              )}
            </div>
            <div className="ui-field">
              <label htmlFor="plan-name">{t("Package Name")}</label>
              <input
                id="plan-name"
                className="ui-input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </div>
            <div className="ui-field ui-field-full">
              <span className="ui-label">{t("desc")}</span>
              <ReactQuill
                className="ui-editor"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                value={editing.description}
                onChange={(value) => setEditing((draft) => ({ ...draft, description: value }))}
              />
            </div>
          </div>
          <footer className="plan-form-actions">
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
              {t("Cancel")}
            </button>
            <button type="button" className="ui-btn" onClick={submit} disabled={saving}>
              {saving ? t("Saving…") : t("Save")}
            </button>
          </footer>
        </section>
      ) : (
        <div className="plan-grid">
          {TYPES.map((item) =>
            data.plans?.[item.type] ? (
              <PlanCard
                key={item.type}
                plan={data.plans[item.type]}
                meta={item}
                usdToCfa={data.usdToCfa}
                onEdit={() => startEdit(data.plans[item.type])}
              />
            ) : null
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={save}
        confirmLabel={t("Change the price")}
      >
        {current && (
          <>
            <p>
              {t("New {{plan}} price: {{from}} → {{to}}", {
                plan: t(meta.label).toLowerCase(),
                from: money(current.price),
                to: money(Number(editing.price)),
              })}
            </p>
            <p className="ui-hint">
              {t("It applies right away to every new subscription: card (a new Stripe price is created), Mobile Money, site and apps. Current card subscribers keep their price; Mobile Money subscribers pay the new price at their next renewal.")}
            </p>
          </>
        )}
      </ConfirmDialog>
    </Page>
  );
};

export default Package;
